import React, { useState, useEffect } from 'react';
import { apiClient, handleApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import Card from '../components/Card';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import './UserManagement.css';

interface User {
  userId: string;
  email: string;
  name: string;
  role: 'admin' | 'editor';
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UpdateUserData {
  name?: string;
  role?: 'admin' | 'editor';
  active?: boolean;
}

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getUsers();
      setUsers(response.users);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId: string, updateData: UpdateUserData) => {
    try {
      setFormLoading(true);
      await apiClient.updateUser(userId, updateData);
      
      // Update local state
      setUsers(users.map(user => 
        user.userId === userId 
          ? { ...user, ...updateData, updatedAt: new Date().toISOString() }
          : user
      ));
      
      setEditingUser(null);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    if (!window.confirm('¿Estás seguro de que quieres desactivar este usuario?')) {
      return;
    }

    try {
      setFormLoading(true);
      await apiClient.deactivateUser(userId);

      // Update local state
      setUsers(users.map(user => 
        user.userId === userId 
          ? { ...user, active: false, updatedAt: new Date().toISOString() }
          : user
      ));
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setFormLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading-container">
          <LoadingSpinner />
          <p>Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <div className="header-content">
          <h1>👥 Gestión de Usuarios</h1>
          <p>Administrar usuarios y permisos del sistema</p>
          <div className="info-banner">
            <span className="info-icon">ℹ️</span>
            <p>Los usuarios se crean manualmente en AWS Cognito. Aquí puedes gestionar roles y estados.</p>
          </div>
        </div>
        <div className="header-actions">
          <Button
            variant="outline"
            onClick={loadUsers}
            disabled={formLoading}
          >
            🔄 Actualizar
          </Button>
        </div>
      </div>

      {error && (
        <Card className="error-card">
          <div className="error-content">
            <span className="error-icon">⚠️</span>
            <div>
              <h3>Error</h3>
              <p>{error}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError(null)}
            >
              ✕
            </Button>
          </div>
        </Card>
      )}

      {/* Users List */}
      <Card className="users-list">
        <div className="list-header">
          <h2>Usuarios del Sistema ({users.length})</h2>
        </div>
        
        {users.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">👤</span>
            <h3>No hay usuarios</h3>
            <p>Los usuarios deben crearse en AWS Cognito</p>
          </div>
        ) : (
          <div className="users-table">
            <div className="table-header">
              <div className="table-cell">Usuario</div>
              <div className="table-cell">Rol</div>
              <div className="table-cell">Estado</div>
              <div className="table-cell">Creado</div>
              <div className="table-cell">Acciones</div>
            </div>
            
            {users.map((user) => (
              <div key={user.userId} className="table-row">
                <div className="table-cell user-info">
                  <div className="user-details">
                    <span className="user-name">{user.name}</span>
                    <span className="user-email">{user.email}</span>
                  </div>
                </div>
                
                <div className="table-cell">
                  {editingUser?.userId === user.userId ? (
                    <select
                      value={user.role}
                      onChange={(e) => {
                        const newRole = e.target.value as 'admin' | 'editor';
                        handleUpdateUser(user.userId, { role: newRole });
                      }}
                      className="inline-select"
                      disabled={formLoading}
                    >
                      <option value="editor">Editor</option>
                      <option value="admin">Administrador</option>
                    </select>
                  ) : (
                    <span className={`role-badge role-${user.role}`}>
                      {user.role === 'admin' ? 'Administrador' : 'Editor'}
                    </span>
                  )}
                </div>
                
                <div className="table-cell">
                  <span className={`status-badge status-${user.active ? 'active' : 'inactive'}`}>
                    {user.active ? '✅ Activo' : '❌ Inactivo'}
                  </span>
                </div>
                
                <div className="table-cell">
                  <span className="date-text">{formatDate(user.createdAt)}</span>
                </div>
                
                <div className="table-cell actions-cell">
                  {user.active && user.userId !== currentUser?.userId && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingUser(editingUser?.userId === user.userId ? null : user)}
                        disabled={formLoading}
                        title="Editar rol de usuario"
                      >
                        ✏️
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeactivateUser(user.userId)}
                        disabled={formLoading}
                        title="Desactivar usuario"
                        className="danger-action"
                      >
                        🚫
                      </Button>
                    </>
                  )}
                  {user.userId === currentUser?.userId && (
                    <span className="current-user-badge">Tú</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default UserManagement;